import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import { FastBillIcon, InvoiceCalculateIcon, InvoiceIcon } from '@c2fo/liquidity/icons';
import { useOfferBuilderContext } from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { useReporting } from '@/reporting';

export interface HowC2foWorksModalProps {
  open: boolean;
  onClose: () => void;
}

const HowC2foWorksModal = ({ open, onClose }: HowC2foWorksModalProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const {
    state: { acquisitionOfferProperties },
  } = useOfferBuilderContext();

  useEffectOnce(() => {
    trackEnterpriseEvent('acquisition-offer::how-c2fo-works-modal::shown', {
      ...(acquisitionOfferProperties?.acquisitionCorrelationId && {
        acquisitionCorrelationId: acquisitionOfferProperties.acquisitionCorrelationId,
      }),
      ...(acquisitionOfferProperties?.urlReferrer && { urlReferrer: acquisitionOfferProperties.urlReferrer }),
    });
  });

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalTitleInfo>{t('howC2foWorks.title')}</ModalTitleInfo>
      <ModalContent>
        <div className="flex flex-col justify-center">
          <p className="mb-4 mt-0 text-center text-sm text-text-secondary">{t('howC2foWorks.subtitle')}</p>
          <div className="flex flex-col gap-8 rounded-md bg-gray-50 p-4">
            <div className="flex gap-4">
              <div className="relative shrink-0">
                <InvoiceIcon className="text-5xl" />
                <span className="absolute -top-1 left-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-medium text-white">
                  1
                </span>
              </div>
              <div>
                <h5 className="m-0 text-base font-semibold">{t('howC2foWorks.step1Title')}</h5>
                <p className="mt-1 text-sm text-text-secondary">{t('howC2foWorks.step1Body')}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="relative shrink-0">
                <InvoiceCalculateIcon className="text-5xl" />
                <span className="absolute -top-1 left-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-medium text-white">
                  2
                </span>
              </div>
              <div>
                <h5 className="m-0 text-base font-semibold">{t('howC2foWorks.step2Title')}</h5>
                <p className="mt-1 text-sm text-text-secondary">{t('howC2foWorks.step2Body')}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="relative shrink-0">
                <FastBillIcon className="text-5xl" />
                <span className="absolute -top-1 left-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-medium text-white">
                  3
                </span>
              </div>
              <div>
                <h5 className="m-0 text-base font-semibold">{t('howC2foWorks.step3Title')}</h5>
                <p className="mt-1 text-sm text-text-secondary">{t('howC2foWorks.step3Body')}</p>
              </div>
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} variant="secondary">
          {t('howC2foWorks.close')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default HowC2foWorksModal;
